import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Footer.css";
import { submitForm } from '../utils/forms';

const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [newsletterError, setNewsletterError] = useState('');

  const onNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('loading');
    setNewsletterError('');
    try {
      const result = await submitForm('newsletter', { email: newsletterEmail });
      setNewsletterStatus(result.fallback ? 'fallback' : 'success');
    } catch (err) {
      setNewsletterError(err.message);
      setNewsletterStatus('error');
    }
  };

  return (
    <footer className="footer">
      <div className="footer-cta">
        <div>
          <span className="section-eyebrow">Ready for enterprise AI?</span>
          <h2>Build a data advantage your business can measure.</h2>
          <p>From board-ready strategy to production AI systems on every cloud — Dalytix delivers.</p>
        </div>
        <div className="footer-cta-actions">
          <Link to="/request-demo" className="btn-primary">Book a Consultation</Link>
          <Link to="/case-studies" className="btn-ghost">View Outcomes</Link>
        </div>
      </div>

      <div className="footer-newsletter">
        <div className="site-container footer-newsletter-grid">
          <div>
            <span className="section-eyebrow">Dalytix Briefing</span>
            <h3>The monthly enterprise AI briefing for data, product, and technology leaders.</h3>
            <p>Architecture patterns, case studies, regulation updates, and the things we wish someone had told us a quarter earlier.</p>
          </div>
          <form className="newsletter-form" onSubmit={onNewsletterSubmit}>
            <label htmlFor="footer-email">Work email</label>
            <div className="newsletter-input-row">
              <input
                id="footer-email"
                type="email"
                placeholder="you@company.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn-primary" disabled={newsletterStatus === 'loading'}>
                {newsletterStatus === 'loading' ? 'Sending...' : 'Subscribe'}
              </button>
            </div>
            {newsletterError && <small className="form-error">{newsletterError}</small>}
            {newsletterStatus === 'success' && <small>Thanks — you are subscribed.</small>}
            {newsletterStatus === 'fallback' && <small>Your email client is ready with a subscription request.</small>}
            {newsletterStatus === 'idle' && <small>One email a month. No spam. Unsubscribe in one click.</small>}
          </form>
        </div>
      </div>

      <div className="footer-container">
        <div className="footer-brand">
          <Link to="/" className="footer-logo" aria-label="Dalytix Home">
            <img src="/images/dalytix-logo-pro.svg" alt="" />
            <span>Dalytix</span>
          </Link>
          <p>
            Senior AI, data engineering, analytics, and cloud consulting for teams that need
            production-ready outcomes for teams modernizing AI, analytics, governance, and cloud data platforms.
          </p>
          <div className="footer-socials">
            <a href="https://www.linkedin.com/company/dalytix" aria-label="LinkedIn" target="_blank" rel="noreferrer">in</a>
            <a href="https://x.com/dalytix" aria-label="X" target="_blank" rel="noreferrer">X</a>
            <a href="https://www.youtube.com/@dalytix" aria-label="YouTube" target="_blank" rel="noreferrer">▶</a>
            <a href="https://github.com/dalytix" aria-label="GitHub" target="_blank" rel="noreferrer">{"</>"}</a>
          </div>
        </div>

        <nav className="footer-column" aria-label="Services">
          <h3>Services</h3>
          <Link to="/services/ai-strategy">AI Strategy</Link>
          <Link to="/services/generative-ai">Generative AI &amp; Agents</Link>
          <Link to="/services/data-engineering">Data Engineering</Link>
          <Link to="/services/mlops">MLOps</Link>
          <Link to="/services/analytics-bi">Analytics &amp; BI</Link>
          <Link to="/services/data-governance">Data Governance</Link>
          <Link to="/services/cloud-modernization">Cloud Modernization</Link>
          <Link to="/services/ai-security">AI Security</Link>
        </nav>

        <nav className="footer-column" aria-label="Industries">
          <h3>Industries</h3>
          <Link to="/industries/financial-services">Financial Services</Link>
          <Link to="/industries/healthcare">Healthcare &amp; Life Sciences</Link>
          <Link to="/industries/retail-cpg">Retail &amp; Consumer Goods</Link>
          <Link to="/industries/manufacturing">Manufacturing</Link>
          <Link to="/industries/energy-utilities">Energy &amp; Utilities</Link>
          <Link to="/industries/insurance">Insurance</Link>
          <Link to="/industries/public-sector">Public Sector</Link>
          <Link to="/industries/telecom">Telecommunications</Link>
        </nav>

        <nav className="footer-column" aria-label="Company">
          <h3>Company</h3>
          <Link to="/about">About Dalytix</Link>
          <Link to="/leadership">Leadership</Link>
          <Link to="/case-studies">Case Studies</Link>
          <Link to="/insights">Insights</Link>
          <Link to="/careers">Careers</Link>
          <Link to="/partners">Partners</Link>
          <Link to="/customers">Customers</Link>
          <Link to="/contact">Contact</Link>
        </nav>

        <div className="footer-column">
          <h3>Offices</h3>
          <span>Altrincham, UK · Headquarters</span>
          <span>New York · Americas Hub</span>
          <span>Remote · Distributed Team</span>
          <a className="footer-mail" href="mailto:hello@dalytix.com">hello@dalytix.com</a>
          <span>+44 7838 593666</span>
        </div>
      </div>

      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} Dalytix Group. All rights reserved.</p>
        <div className="footer-legal">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/cookies">Cookies</Link>
          <Link to="/dpa">DPA</Link>
          <Link to="/trust">Trust</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
